// src/components/projects/ProjectKpiDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faListUl, faLock,
  faMoneyBillWave, faSackDollar, faCube, faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';

import { API_ENDPOINTS } from '../../apiConfig';
import { MODULES } from '../../constants/globalConstants';
import {
  readUserFromLocalStorage,
  hasPermission,
  getModulePermissionRow,
} from '../../utils/permissions';
import type { UserDTO } from '../auth/AuthApi';

/* =========================
   Types for API JSON payload
   ========================= */
type UUID = string;

type CompletionRow = {
  project_id: UUID;
  project_name: string;
  completion_percentage: number;
};

type TimelineRow = {
  project_id: UUID;
  project_name: string;
  project_type: string | null;
  planned_start: string | null;
  planned_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
};

type BudgetUtilRow = {
  project_id: UUID;
  project_name: string;
  actual_cost: number;
  estimated_cost: number;
  bid_cost: number;
  total_budget: number;
  budget_utilization_percentage: number;
};

type DelayedProjectRow = {
  project_id: UUID;
  project_name: string;
  project_type: string | null;
  status?: string | null;
  given_end_date: string | null;
  actual_end_date: string | null;
  days_delayed: number;
  alert_type: 'DELAYED';
};

type OverBudgetRow = {
  project_id: UUID;
  project_name: string;
  total_estimated_cost: number;
  total_actual_cost: number;
  total_bid_cost: number;
  over_budget_amount: number;
  over_budget_percentage: number;
};

type UnderBudgetRow = {
  project_id: UUID;
  project_name: string;
  total_estimated_cost: number;
  total_actual_cost: number;
  total_bid_cost: number;
  under_budget_amount: number;
  under_budget_percentage: number;
};

type ValueSummary = {
  total_project_bid_value: number;
  total_project_actual_value: number;
  total_project_budget_value: number;
  bid_vs_actual_percentage_diff: number;
};

type ProjectKpiResponse = {
  project_completion_percentage: CompletionRow[];
  planned_vs_actual_timeline:   TimelineRow[];
  budget_utilization:           BudgetUtilRow[];
  delayed_projects:             DelayedProjectRow[];
  over_budget_projects:         OverBudgetRow[];
  under_budget_projects:        UnderBudgetRow[];
  active_projects_value_summary:   ValueSummary;
  completed_projects_value_summary: ValueSummary;
};

/* =========================
   Small API helper
   ========================= */
const API_DASHBOARD = `${API_ENDPOINTS.project}/kpi`;
async function getProjectKpis(tenantId?: string, tenantName?: string) {
  return axios.get<ProjectKpiResponse>(`${API_DASHBOARD}/project-kpi`, {
    params: { tenantId, tenantName },
  });
}

/* =========================
   Utilities
   ========================= */
const truncateName = (s: string, n = 30) => (s?.length > n ? s.slice(0, n - 1) + '…' : s || '—');
const fmtINR = (v: number) => `₹${Number(v || 0).toLocaleString()}`;

/* =========================
   Card helpers
   ========================= */
const StatCard: React.FC<{
  icon: any;
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  tone?: 'blue'|'green'|'red'|'amber'|'slate';
}> = ({ icon, title, value, subtitle, tone='slate' }) => {
  const toneMap: Record<string, string> = {
    blue:  'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red:   'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${toneMap[tone]} shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium opacity-80">{title}</div>
          <div className="text-2xl font-bold">{value}</div>
          {subtitle && <div className="text-xs opacity-70">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
};

/* =========================
   Custom tooltips
   ========================= */

// Completion % tooltip (full project name)
const CompletionTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
      <div className="font-semibold mb-1">{row?.fullName || row?.name}</div>
      <div>Completion: {row?.value}%</div>
    </div>
  );
};

// Budget Utilization tooltip (full project name)
const BudgetTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
      <div className="font-semibold mb-1">{row?.fullName || row?.name}</div>
      <div>Actual: {fmtINR(row?.actual)}</div>
      <div>Bid: {fmtINR(row?.bid)}</div>
      <div>Total Budget: {fmtINR(row?.budget)}</div>
    </div>
  );
};

// Timeline (durations) tooltip (full project name)
const TimelineTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
      <div className="font-semibold mb-1">{row?.fullName || row?.name}</div>
      <div><span className="text-cyan-600 font-medium">Planned Duration</span>: {row?.planned_days ?? 0} days</div>
      <div><span className="text-violet-600 font-medium">Actual Duration</span>: {row?.actual_days ?? 0} days</div>
    </div>
  );
};

/* =========================
   Main Dashboard
   ========================= */
const ProjectKpiDashboard: React.FC = () => {
  const navigate = useNavigate();

  const user = readUserFromLocalStorage<UserDTO>();
  const tenantId = user?.tenantId;
  const tenantName = user?.tenantName;

  // Role-based access
  const projectsPermRow = useMemo(
    () => getModulePermissionRow(user || null, MODULES.PROJECTS),
    [user]
  );

  const canSeeDashboard = useMemo(() => {
    const viewOrReport = hasPermission(user || null, MODULES.PROJECTS, ['view', 'report'], 'any');
    const visibleFlag =
      typeof projectsPermRow?.visibleInDashboard === 'boolean'
        ? !!projectsPermRow?.visibleInDashboard
        : true;
    return viewOrReport && visibleFlag;
  }, [user, projectsPermRow]);

  const canSeeProjectListBtn = useMemo(
    () => hasPermission(user || null, MODULES.PROJECTS, ['view', 'edit', 'create'], 'any'),
    [user]
  );

  // Data
  const [kpis, setKpis] = useState<ProjectKpiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!canSeeDashboard) {
      setLoading(false);
      setKpis(null);
      setError('');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await getProjectKpis(tenantId, tenantName);
        setKpis(res.data);
      } catch (e) {
        setError('Failed to load Project KPI data.');
      } finally {
        setLoading(false);
      }
    })();
  }, [canSeeDashboard, tenantId, tenantName]);

  // Derived datasets (store fullName for tooltips; show truncated name on axis)
  const completionData = (kpis?.project_completion_percentage || [])
    .map(r => ({
      fullName: r.project_name,
      name: truncateName(r.project_name, 30),
      value: r.completion_percentage,
    }))
    .sort((a, b) => b.value - a.value);

  const budgetData = (kpis?.budget_utilization || []).map(r => ({
    fullName: r.project_name,
    name: truncateName(r.project_name, 30),
    actual: Number(r.actual_cost || 0),
    bid:    Number(r.bid_cost || 0),
    budget: Number(r.total_budget || 0),
  }));

  const delayedSorted = (kpis?.delayed_projects || [])
    .sort((a,b) => (b.days_delayed ?? 0) - (a.days_delayed ?? 0));

  const overBudgetSorted = (kpis?.over_budget_projects || [])
    .sort((a,b) => (b.over_budget_percentage ?? 0) - (a.over_budget_percentage ?? 0));

  const underBudgetSorted = (kpis?.under_budget_projects || [])
    .sort((a,b) => (b.under_budget_percentage ?? 0) - (a.under_budget_percentage ?? 0));

  const timelineDurationData = (kpis?.planned_vs_actual_timeline || []).map(row => {
    const ps = row.planned_start ? new Date(row.planned_start) : null;
    const pe = row.planned_end   ? new Date(row.planned_end)   : null;
    const as = row.actual_start  ? new Date(row.actual_start)  : null;
    const ae = row.actual_end    ? new Date(row.actual_end)    : null;

    const plannedDays =
      ps && pe ? Math.max(0, Math.round((pe.getTime() - ps.getTime()) / 86400000)) : 0;
    const actualDays =
      as && ae ? Math.max(0, Math.round((ae.getTime() - as.getTime()) / 86400000)) : 0;

    return {
      fullName: row.project_name,
      name: truncateName(row.project_name, 30),
      planned_days: plannedDays,
      actual_days: actualDays,
    };
  });

  // Unauthorized gate
  if (!canSeeDashboard) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-white border rounded-xl p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full border flex items-center justify-center text-slate-600">
              <FontAwesomeIcon icon={faLock} />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-1">Access Restricted</h2>
          <p className="text-slate-600">
            You don’t have permission to view the Projects dashboard. If you believe this is an error,
            please contact your administrator.
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Authorized UI
  return (
    <div className="p-6 space-y-6">
      {/* Header + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back
          </button>
        </div>
        <h1 className="text-2xl font-bold">Project Dashboard</h1>
        <div className="flex items-center gap-2">
          {canSeeProjectListBtn && (
            <button
              className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate('/projects/list')}
            >
              <FontAwesomeIcon icon={faListUl} className="mr-2" />
              Project List
            </button>
          )}
        </div>
      </div>

      {/* Top summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatCard
          icon={faCube}
          title="Projects Returned"
          value={completionData.length}
          subtitle="Total count in completion dataset"
          tone="blue"
        />
        <StatCard
          icon={faMoneyBillWave}
          title="Active: Bid vs Actual"
          value={
            kpis
              ? `${fmtINR(kpis.active_projects_value_summary?.total_project_bid_value ?? 0)} vs ${fmtINR(kpis.active_projects_value_summary?.total_project_actual_value ?? 0)}`
              : '—'
          }
          subtitle={
            kpis
              ? `${kpis.active_projects_value_summary?.bid_vs_actual_percentage_diff ?? 0}% (Actual vs Bid)`
              : ''
          }
          tone={(kpis?.active_projects_value_summary?.bid_vs_actual_percentage_diff ?? 0) >= 0 ? 'red' : 'green'}
        />
        <StatCard
          icon={faSackDollar}
          title="Completed: Bid vs Actual"
          value={
            kpis
              ? `${fmtINR(kpis.completed_projects_value_summary?.total_project_bid_value ?? 0)} vs ${fmtINR(kpis.completed_projects_value_summary?.total_project_actual_value ?? 0)}`
              : '—'
          }
          subtitle={
            kpis
              ? `${kpis.completed_projects_value_summary?.bid_vs_actual_percentage_diff ?? 0}% (Actual vs Bid)`
              : ''
          }
          tone={(kpis?.completed_projects_value_summary?.bid_vs_actual_percentage_diff ?? 0) >= 0 ? 'red' : 'green'}
        />
      </div>

      {/* Completion % chart */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Project Completion %</h2>
          <div className="text-slate-500 text-sm">Higher is better</div>
        </div>
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={completionData}
                margin={{ left: 16, right: 16, top: 8, bottom: 32 }}
                barCategoryGap={10}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  tick={{ fontSize: 11 }}
                  angle={-30}
                  height={70}
                  textAnchor="end"
                />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={50} />
                <Tooltip content={<CompletionTooltip />} />
                <Legend />
                <Bar dataKey="value" name="Completion %" fill="#4f46e5" maxBarSize={36}>
                  <LabelList dataKey="value" position="top" formatter={(v) => `${Number(v)}%`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Budget Utilization */}
      <div className="bg-white rounded-xl border p-4 shadow-sm ">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Budget Utilization</h2>
          <div className="text-slate-500 text-sm">Actual vs Bid vs Total Budget</div>
        </div>
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <div className="h-96"  >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={budgetData}
                margin={{ left: 16, right: 16, top: 8, bottom: 32 }}
                barCategoryGap={18}
                barGap={6}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  tick={{ fontSize: 11 }}
                  angle={-30}
                  height={70}
                  textAnchor="end"
                />
                <YAxis
                  width={80}
                  tickFormatter={(v) => `₹${Number(v).toLocaleString()}`}
                />
                <Tooltip content={<BudgetTooltip />} />
                <Legend />
                <Bar dataKey="actual" name="Actual Cost" fill="#ef4444" maxBarSize={40}>
                  <LabelList dataKey="actual" position="top" formatter={(label) => (typeof label === 'number' ? `₹${Number(label).toLocaleString()}` : label)} />
                </Bar>
                <Bar dataKey="bid" name="Bid Cost" fill="#10b981" maxBarSize={40}>
                  <LabelList dataKey="bid" position="top" formatter={(label) => (typeof label === 'number' ? `₹${Number(label).toLocaleString()}` : label)} />
                </Bar>
                <Bar dataKey="budget" name="Total Budget" fill="#3b82f6" maxBarSize={40}>
                  <LabelList dataKey="budget" position="top" formatter={(label) => (typeof label === 'number' ? `₹${Number(label).toLocaleString()}` : label)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Delayed Projects with extra details */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Most Delayed Projects</h2>
          <div className="text-slate-500 text-sm flex items-center gap-2">
            <FontAwesomeIcon icon={faClockRotateLeft} /> Sorted by days delayed
          </div>
        </div>
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : delayedSorted.length === 0 ? (
          <div className="text-slate-500">No delayed projects 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Project</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Given End</th>
                  <th className="p-2 text-left">Actual End</th>
                  <th className="p-2 text-right">Days Delayed</th>
                </tr>
              </thead>
              <tbody>
                {delayedSorted.map((row) => (
                  <tr key={row.project_id} className="border-t">
                    <td className="p-2">{row.project_name}</td>
                    <td className="p-2">{row.project_type || '—'}</td>
                    <td className="p-2">{row.status || '—'}</td>
                    <td className="p-2">{row.given_end_date ? new Date(row.given_end_date).toLocaleDateString() : '—'}</td>
                    <td className="p-2">{row.actual_end_date ? new Date(row.actual_end_date).toLocaleDateString() : '—'}</td>
                    <td className="p-2 text-right">{row.days_delayed ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Planned vs Actual: durations (days) */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Planned vs Actual Timeline (Durations)</h2>
          <div className="text-slate-500 text-sm">Bars show duration in days</div>
        </div>
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={timelineDurationData}
                margin={{ left: 16, right: 16, top: 8, bottom: 32 }}
                barCategoryGap={18}
                barGap={6}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  tick={{ fontSize: 11 }}
                  angle={-30}
                  height={70}
                  textAnchor="end"
                />
                <YAxis tickFormatter={(v) => `${v}d`} width={50} />
                <Tooltip content={<TimelineTooltip />} />
                <Legend />
                <Bar dataKey="planned_days" name="Planned Duration" fill="#06b6d4" maxBarSize={40}>
                  <LabelList dataKey="planned_days" position="top" formatter={(label) => (typeof label === 'number' ? `${label}d` : label)} />
                </Bar>
                <Bar dataKey="actual_days"  name="Actual Duration"  fill="#8b5cf6" maxBarSize={40}>
                  <LabelList dataKey="actual_days" position="top" formatter={(label) => (typeof label === 'number' ? `${label}d` : label)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Over & Under Budget Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Top Over-Budget Projects</h2>
            <div className="text-rose-600 text-sm">Higher % is worse</div>
          </div>
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : overBudgetSorted.length === 0 ? (
            <div className="text-slate-500">No over-budget projects 🎉</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 text-left">Project</th>
                    <th className="p-2 text-right">Actual</th>
                    <th className="p-2 text-right">Bid</th>
                    <th className="p-2 text-right">Over Amt</th>
                    <th className="p-2 text-right">% Over</th>
                  </tr>
                </thead>
                <tbody>
                  {overBudgetSorted.map((r) => (
                    <tr key={r.project_id} className="border-t">
                      <td className="p-2">{r.project_name}</td>
                      <td className="p-2 text-right">{fmtINR(r.total_actual_cost)}</td>
                      <td className="p-2 text-right">{fmtINR(r.total_bid_cost)}</td>
                      <td className="p-2 text-right">{fmtINR(r.over_budget_amount)}</td>
                      <td className="p-2 text-right">{Number(r.over_budget_percentage).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Top Under-Budget Projects</h2>
            <div className="text-emerald-600 text-sm">Higher % is better</div>
          </div>
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : underBudgetSorted.length === 0 ? (
            <div className="text-slate-500">No under-budget projects</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 text-left">Project</th>
                    <th className="p-2 text-right">Actual</th>
                    <th className="p-2 text-right">Bid</th>
                    <th className="p-2 text-right">Under Amt</th>
                    <th className="p-2 text-right">% Under</th>
                  </tr>
                </thead>
                <tbody>
                  {underBudgetSorted.map((r) => (
                    <tr key={r.project_id} className="border-t">
                      <td className="p-2">{r.project_name}</td>
                      <td className="p-2 text-right">{fmtINR(r.total_actual_cost)}</td>
                      <td className="p-2 text-right">{fmtINR(r.total_bid_cost)}</td>
                      <td className="p-2 text-right">{fmtINR(r.under_budget_amount)}</td>
                      <td className="p-2 text-right">{Number(r.under_budget_percentage).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectKpiDashboard;